import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { extractSubdomain, getStoreName } from '../utils/subdomain';
import Loading from '../components/Loading';
import './Login.css';

/**
 * Caminho INTERNO para onde voltar depois do login, ou null.
 *
 * `startsWith('/')` sozinho não basta e era o que havia aqui: `//evil.com` também começa
 * com barra e o browser trata como URL protocolo-relativa — ou seja, redirect aberto para
 * fora do domínio, num parâmetro que qualquer um monta no link. `/\evil.com` cai no mesmo
 * buraco em alguns browsers. Como o destino passou a valer para os domínios de cliente e
 * não só para o admin, a superfície ficou maior e a checagem tinha de ficar certa.
 */
function destinoSeguro(caminho) {
    if (!caminho || typeof caminho !== 'string') return null;
    if (!caminho.startsWith('/')) return null;
    if (caminho.startsWith('//') || caminho.startsWith('/\\')) return null;
    if (caminho.startsWith('/login')) return null;   // não voltar para a própria tela
    return caminho;
}

const Login = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [storeName, setStoreName] = useState('Aliado');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectPath = searchParams.get('redirect');

    const checkAuth = useCallback(async () => {
        try {
            const authResult = await apiService.checkAuth();
            if (authResult.authenticated) {
                // Quem já tem sessão e chega em /login?redirect=X vai para X, não para a
                // home: é o caso de abrir o link do dashboard numa aba nova.
                navigate(destinoSeguro(redirectPath) || '/');
            }
        } catch (error) {
            // usuário não autenticado, permanecer na página de login
        }
    }, [navigate, redirectPath]);

    const loadStoreName = useCallback(async () => {
        try {
            const stores = await apiService.getStores();
            const name = getStoreName(stores);
            setStoreName(name);
        } catch (error) {
            console.error('Erro ao carregar nome da loja:', error);
        }
    }, []);

    useEffect(() => {
        checkAuth();
        loadStoreName();
    }, [checkAuth, loadStoreName]);

    // Retorno do fluxo SSO Google. O callback do servidor já gravou a sessão e
    // devolveu o browser para /login?sso=ok (ou ?sso_error=...). Aqui finalizamos:
    // gravamos o localStorage (igual ao login por senha) e entramos.
    useEffect(() => {
        const ssoError = searchParams.get('sso_error');
        if (ssoError) {
            setError(ssoError);
            return;
        }
        if (searchParams.get('sso') === 'ok') {
            setLoading(true);
            apiService.completeSsoLogin()
                .then((data) => {
                    if (data && data.success) {
                        // Também no SSO. Sem isto, entrar pelo Google jogava na home mesmo
                        // tendo vindo de um link de dashboard.
                        navigate(destinoSeguro(redirectPath) || '/');
                    } else {
                        setError('Não foi possível concluir o login com Google.');
                        setLoading(false);
                    }
                })
                .catch(() => {
                    setError('Não foi possível concluir o login com Google.');
                    setLoading(false);
                });
        }
    }, [searchParams, navigate, redirectPath]);

    // SSO no painel admin E nos painéis de loja. O botão aparece sempre que há um
    // subdomínio resolvido (admin ou loja); só some em localhost/sem subdomínio, onde
    // não há empresa contra a qual autenticar. Quem pode de fato entrar é decidido na
    // API (domínio do e-mail + permissão na empresa), não aqui.
    const showGoogleSso = !!extractSubdomain();

    const handleGoogleLogin = () => {
        // O destino precisa atravessar a ida ao Google, e o `?redirect=` DESTA url não
        // atravessa: o browser sai para o accounts.google.com e volta em /login?sso=ok,
        // sem os parâmetros de agora. Mandamos para o /start, que o guarda na sessão do
        // Express junto de state/nonce e o devolve no callback.
        const destino = destinoSeguro(redirectPath);
        window.location.href = destino
            ? `/auth/google/start?redirect=${encodeURIComponent(destino)}`
            : '/auth/google/start';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Limpar erro quando usuário começar a digitar
        if (error) {
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.username.trim() || !formData.password.trim()) {
            setError('Usuário e senha são obrigatórios');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const result = await apiService.login(formData.username, formData.password);

            if (result.success) {
                // Volta para a URL que a pessoa pediu, em QUALQUER domínio. Era
                // restrito ao portal admin, e o efeito era o link direto de um dashboard
                // (leroymerlin.alia.do/analytic-dashboard/<slug>) parar na home depois do
                // login — justamente o caso em que o link foi compartilhado para abrir
                // alguma coisa específica.
                navigate(destinoSeguro(redirectPath) || '/');
            } else {
                setError(result.message || 'Erro ao fazer login');
            }
        } catch (error) {
            setError(error.message || 'Erro interno. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }; const subdomain = extractSubdomain();

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card">
                    {/* Header */}
                    <div className="login-header">
                        <img
                            src="/assets/aliado-logo.png"
                            alt="Aliado"
                            className="login-logo"
                        />
                        <h1 className="login-title">{storeName}</h1>
                        {subdomain && (
                            <p className="login-subtitle">
                                Acesso para: <strong>{subdomain}</strong>
                            </p>
                        )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <div className="alert alert-danger">
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="username" className="form-label">
                                Usuário
                            </label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Digite seu usuário"
                                disabled={loading}
                                autoComplete="username"
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                Senha
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Digite sua senha"
                                disabled={loading}
                                autoComplete="current-password"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-lg w-100"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Entrando...
                                </>
                            ) : (
                                'Entrar'
                            )}
                        </button>
                    </form>

                    {/* SSO Google — painel admin e painéis de loja */}
                    {showGoogleSso && (
                        <div className="login-sso">
                            <div className="login-sso-divider"><span>ou</span></div>
                            <button
                                type="button"
                                className="btn-google"
                                onClick={handleGoogleLogin}
                                disabled={loading}
                            >
                                <span className="btn-google-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2582h2.9086c1.7018-1.5668 2.6836-3.874 2.6836-6.6151z"/>
                                        <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9086-2.2582c-.8059.54-1.8368.859-3.0478.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"/>
                                        <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.964 10.71z"/>
                                        <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"/>
                                    </svg>
                                </span>
                                <span className="btn-google-text">Entrar com Google</span>
                            </button>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="login-footer">
                        <p>
                            <small>
                                IA para o Varejo - Aliado © 2026. Todos os direitos reservados.
                            </small>
                        </p>
                        {subdomain && (
                            <p>
                                <small>
                                    Subdomínio: {subdomain}
                                </small>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {loading && (
                <Loading
                    message="Autenticando..."
                    fullScreen
                />
            )}
        </div>
    );
};

export default Login;